import { X } from "lucide-react";
import { useState } from "react";

import { DialogoCambiosSinGuardar } from "@/components/DialogoCambiosSinGuardar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { urlDeLaFotoDeLaFila, type MaquinariaDeInforme } from "@/lib/informes";
import { cn } from "@/lib/utils";

import {
  GrupoDeIdentificacionDeMaquina,
  GrupoDeValorizacionDeMaquina,
  ValorComercialEstimado,
} from "./CamposDeMaquina";
import { RotuloDeGrupo } from "./CamposDeConstruccion";
import { mismosCampos } from "./DialogoDeEdicionDeFila";
import { FotosDeLaFila } from "./FotosDeLaFila";
import { formularioDesde, type FormularioDeMaquina } from "./maquinaria";

/** Lo que el modal entrega al guardar. */
export interface GuardadoDeLaMaquina {
  formulario: FormularioDeMaquina;
  validada: boolean;
}

interface DialogoDeMaquinaProps {
  /** La máquina que se abre, o `null` con el modal cerrado. */
  maquina: MaquinariaDeInforme | null;
  informeId: number;
  ocupada: boolean;
  onGuardar: (maquina: MaquinariaDeInforme, guardado: GuardadoDeLaMaquina) => void;
  onQuitar: (maquina: MaquinariaDeInforme) => void;
  onCerrar: () => void;
  onSubirFoto: (maquinariaId: number, archivo: File) => void;
  onQuitarFoto: (maquinariaId: number, fotoId: number) => void;
  onLeyendaDeFoto?: (maquinariaId: number, fotoId: number, leyenda: string | null) => void;
}

/**
 * El modal "Editar máquina o equipo": los campos, el valor comercial estimado,
 * las fotos y, al pie, la validación de la fila.
 *
 * SE REMONTA POR FILA (`key`): el estado de lo que se está escribiendo nace de
 * la fila que se abre y muere con el modal, así que abrir otra nunca hereda un
 * borrador ajeno.
 */
export function DialogoDeMaquina(props: DialogoDeMaquinaProps) {
  return props.maquina === null ? null : (
    <Contenido key={props.maquina.id} {...props} maquina={props.maquina} />
  );
}

function Contenido({
  maquina,
  informeId,
  ocupada,
  onGuardar,
  onQuitar,
  onCerrar,
  onSubirFoto,
  onQuitarFoto,
  onLeyendaDeFoto,
}: DialogoDeMaquinaProps & { maquina: MaquinariaDeInforme }) {
  // La fila llega de la caché y cambia de identidad con cada refetch (por
  // ejemplo, al subir una foto): lo que se escribe no se pisa, así que el punto
  // de partida se toma UNA vez, al abrir.
  const [inicial] = useState(() => formularioDesde(maquina));
  const [formulario, setFormulario] = useState<FormularioDeMaquina>(inicial);
  const [casilla, setCasilla] = useState(maquina.validada);
  const [casillaTocada, setCasillaTocada] = useState(false);
  const [rechazoDeFoto, setRechazoDeFoto] = useState<string | null>(null);
  const [confirmandoCierre, setConfirmandoCierre] = useState(false);
  const [confirmandoQuitar, setConfirmandoQuitar] = useState(false);

  const cambio = !mismosCampos(formulario, inicial);
  const hayCambios = cambio || casillaTocada;

  /* LO VALIDADO NO SOBREVIVE A UN CAMBIO: si la fila estaba validada y se tocó
     un dato, vuelve a quedar sin validar — salvo que la persona haya vuelto a
     marcar la casilla en el modal, que es decir "ya lo revisé". */
  const vuelveASinValidar = maquina.validada && cambio && !casillaTocada;
  const validadaAlGuardar = vuelveASinValidar ? false : casilla;

  function escribir(cambios: Partial<FormularioDeMaquina>) {
    setFormulario((actual) => ({ ...actual, ...cambios }));
  }

  function pedirCierre() {
    if (hayCambios) setConfirmandoCierre(true);
    else onCerrar();
  }

  function guardar() {
    onGuardar(maquina, { formulario, validada: validadaAlGuardar });
  }

  const comunes = { idPrefijo: "maquina-edicion", formulario, ocupada, escribir };
  const fotos = maquina.fotos?.length ?? 0;

  return (
    <>
      <Dialog open onOpenChange={(abierto) => !abierto && pedirCierre()}>
        <DialogContent
          showCloseButton={false}
          className="max-h-[calc(100vh-3.5rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-[55rem]"
        >
          <div className="border-divisor flex flex-none items-center gap-3 border-b-[1.5px] px-5 py-4">
            <div className="min-w-0 flex-1">
              <div className="text-ink-hint text-[0.65625rem] font-extrabold tracking-[0.07em] uppercase">
                Editar máquina o equipo
              </div>
              <DialogTitle className="text-ink mt-1 truncate text-lg font-extrabold">
                {maquina.nombre}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Los datos se guardan con «Guardar máquina». Las fotos de una máquina ya guardada se
                guardan apenas las sueltas.
              </DialogDescription>
            </div>

            {vuelveASinValidar ? (
              <span className="bg-warning-soft text-warning-soft-foreground rounded-md px-2.5 py-1 text-xs font-semibold">
                Al guardar cambios vuelve a quedar sin validar
              </span>
            ) : null}

            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Cerrar"
              title="Cerrar"
              onClick={pedirCierre}
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          </div>

          <div className="flex flex-col gap-3.5 overflow-y-auto px-5 py-4.5">
            <RotuloDeGrupo>Identificación</RotuloDeGrupo>
            <GrupoDeIdentificacionDeMaquina {...comunes} />

            <div className="bg-divisor-suave h-px" />
            <RotuloDeGrupo>Valorización</RotuloDeGrupo>
            <GrupoDeValorizacionDeMaquina {...comunes} columnas={3} />
            <ValorComercialEstimado formulario={formulario} />

            <div className="bg-divisor-suave h-px" />
            <FotosDeLaFila
              titulo="Fotos"
              etiquetaDelSelector="Adjuntar fotos de la máquina o equipo"
              ocupada={ocupada}
              editando
              pendientes={[]}
              guardadas={maquina.fotos ?? []}
              urlDe={(foto) =>
                urlDeLaFotoDeLaFila("maquinarias", informeId, maquina.id, foto.id, foto.hash)
              }
              alt={`Foto de ${maquina.nombre}`}
              error={rechazoDeFoto}
              // La fila YA existe: cada foto se va sola, sin esperar el botón de
              // abajo — las imágenes se guardan al soltarlas.
              onElegidas={(archivos) => archivos.forEach((a) => onSubirFoto(maquina.id, a))}
              onRechazo={setRechazoDeFoto}
              onQuitarPendiente={() => {}}
              onQuitarGuardada={(fotoId) => onQuitarFoto(maquina.id, fotoId)}
              onLeyenda={
                onLeyendaDeFoto
                  ? (fotoId, leyenda) => onLeyendaDeFoto(maquina.id, fotoId, leyenda)
                  : undefined
              }
            />
          </div>

          <div className="border-divisor bg-card flex flex-none flex-wrap items-center gap-2.5 border-t-[1.5px] px-5 py-3">
            <label
              className={cn(
                "flex w-fit items-center gap-2 text-xs font-bold",
                casilla ? "text-success-soft-foreground" : "text-ink-body",
              )}
            >
              <Checkbox
                checked={casilla}
                disabled={ocupada}
                onCheckedChange={(v) => {
                  setCasilla(v === true);
                  setCasillaTocada(true);
                }}
              />
              Información validada
            </label>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={ocupada}
              className="text-danger-soft-foreground hover:text-danger-soft-foreground"
              onClick={() => setConfirmandoQuitar(true)}
            >
              Quitar
            </Button>

            <div className="ml-auto flex gap-2.5">
              <Button type="button" variant="outline" onClick={pedirCierre} disabled={ocupada}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={guardar}
                disabled={ocupada || formulario.nombre.trim() === ""}
              >
                {ocupada ? <Spinner /> : null}
                Guardar máquina
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DialogoCambiosSinGuardar
        abierto={confirmandoCierre}
        descripcion={
          "Vas a perder los cambios sin guardar en esta máquina. Las fotos que ya se " +
          "subieron no se deshacen: siguen en el informe aunque descartes el resto."
        }
        guardando={ocupada}
        onGuardarYSalir={() => {
          setConfirmandoCierre(false);
          guardar();
        }}
        onSalirSinGuardar={() => {
          setConfirmandoCierre(false);
          onCerrar();
        }}
        onCancelar={() => setConfirmandoCierre(false)}
      />

      {/* QUITAR UNA MÁQUINA SE LLEVA SUS FOTOS Y NO SE DESHACE, así que se
          pregunta. Es el mismo criterio con el que el catálogo de comparables
          confirma el descarte y no lo hace desde un ícono de 30 px. */}
      <AlertDialog open={confirmandoQuitar} onOpenChange={setConfirmandoQuitar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar esta máquina del informe?</AlertDialogTitle>
            <AlertDialogDescription>
              Se va “{maquina.nombre}” junto con sus {fotos} foto{fotos === 1 ? "" : "s"}. No se
              puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmandoQuitar(false);
                onQuitar(maquina);
              }}
            >
              Quitar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
